import logger from '@eventbrite/client-logger';
import { gettext } from '@eventbrite/i18n';
import { Stripe } from '@stripe/stripe-js';
import { StripeButtonHandleErrorCallback } from './StripeButton';

const _handleStripeNextAction = (
    stripe: Stripe,
    paymentIntentClientSecret: string,
    handleErrorCallback: StripeButtonHandleErrorCallback,
) => {
    const errorMessage = gettext(
        'Unexpected error when paying with Stripe. Try another payment method',
    );

    return stripe
        .handleNextAction({ clientSecret: paymentIntentClientSecret })
        .then((result) => {
            if (result.error) {
                logger.error(result.error.message || 'Unknown error');
                return handleErrorCallback(errorMessage);
            }
            if (!result.paymentIntent) {
                logger.error('Cannot process payment: no payment intent.');
                return handleErrorCallback(errorMessage);
            }
        })
        .catch((error) => handleErrorCallback(error.message));
};

export const useHandleRedirectedPayment = (stripe: Stripe | null) => {
    const errorMessage = gettext(
        'Unexpected error when paying with Stripe. Try another payment method',
    );

    return async (
        paymentIntentClientSecret: string,
        handleErrorCallback: StripeButtonHandleErrorCallback,
    ) => {
        // Typescript should take care of enforcing both arguments are not null or undefined
        // But we're calling this callback from Javascript, so acting secure
        if (!handleErrorCallback) {
            logger.error('No handleErrorCallback received.');
            throw Error(errorMessage.toString());
        }

        if (!stripe || !paymentIntentClientSecret) {
            logger.error(
                stripe
                    ? 'Cannot process payment: no payment intent.'
                    : "There's no stripe object.",
            );
            return handleErrorCallback(errorMessage);
        }

        try {
            return _handleStripeNextAction(
                stripe,
                paymentIntentClientSecret,
                handleErrorCallback,
            );
        } catch (error) {
            logger.error(error.message);
            return handleErrorCallback(errorMessage);
        }
    };
};
